import { useCallback, useMemo } from 'react';

import { LoadingOutlined, SearchOutlined } from '@ant-design/icons';
import { Input, Space, Spin, Tooltip } from 'antd';
import cx from 'classnames';

import { ReactComponent as GridViewIcon } from '../../../../../assets/svgs/grid-view-icon.svg';
import { ReactComponent as TableViewIcon } from '../../../../../assets/svgs/list-view-icon.svg';
import { useAuth } from '../../../../../hooks/useAuth';
import { UserPermissions } from '../../../../../util';
import CreateNewProjectButton from '../CreateNewProjectButton';
import { OVRItemsListViewType } from '../OVRProjectItemsList';

const antSpinIcon = <LoadingOutlined style={{ fontSize: 18 }} spin />;

interface OVRHeaderSectionProps {
  viewType: OVRItemsListViewType;
  searchQuery: string;
  searchingOvrProjects: boolean;
  onViewTypeChange: (type: OVRItemsListViewType) => void;
  onSearchChange: (query: string) => void;
}

const OVRHeaderSection = ({
  viewType,
  searchQuery,
  searchingOvrProjects,
  onViewTypeChange,
  onSearchChange,
}: OVRHeaderSectionProps) => {
  const { hasPermission } = useAuth();

  const updateOVRListViewType = useCallback(
    (type: OVRItemsListViewType) => {
      onViewTypeChange(type);
      localStorage.setItem('ovr-list-view-type', type);
    },
    [onViewTypeChange]
  );

  const headerSectionRight = useMemo(
    (): JSX.Element => (
      <Space direction="horizontal" size="middle" align="center">
        <Tooltip title="Grid view" mouseEnterDelay={0.2} mouseLeaveDelay={0}>
          <div
            className={cx('ovr-view-type-icon', {
              selected: viewType === OVRItemsListViewType.Grid,
            })}
            onClick={() => updateOVRListViewType(OVRItemsListViewType.Grid)}
          >
            <GridViewIcon color="#000" />
          </div>
        </Tooltip>
        <Tooltip title="Table view" mouseEnterDelay={0.2} mouseLeaveDelay={0}>
          <div
            className={cx('ovr-view-type-icon', {
              selected: viewType === OVRItemsListViewType.Table,
            })}
            onClick={() => updateOVRListViewType(OVRItemsListViewType.Table)}
          >
            <TableViewIcon />
          </div>
        </Tooltip>
        <Input
          allowClear={!searchingOvrProjects}
          value={searchQuery}
          onChange={(e) => onSearchChange(e.target.value)}
          placeholder="Search for a project..."
          prefix={<SearchOutlined style={{ color: 'rgba(0,0,0,.45)' }} />}
          suffix={searchingOvrProjects && <Spin indicator={antSpinIcon} />}
        />
        {hasPermission(UserPermissions.OvrProjectsCreate) ? (
          <CreateNewProjectButton />
        ) : null}
      </Space>
    ),
    [
      viewType,
      searchingOvrProjects,
      searchQuery,
      updateOVRListViewType,
      onSearchChange,
      hasPermission,
    ]
  );

  return headerSectionRight;
};

export default OVRHeaderSection;
